//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 29/01/2025
// Version       : 1
// Description   : Tabla con las patentes de los vehículos y la cantidad de observaciones de su ultima revisión
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import Navbar from "../../Navbar";
import { MaterialReactTable } from "material-react-table";
// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Button,
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";
import Badge from "@mui/material/Badge";

// ICONOS
import AssignmentIcon from "@mui/icons-material/Assignment";
import ObservacionesRevision from "./ObservacionesRevision";
import { logEventos } from "../../../services/services";


function PatentesObservaciones() {
  const globalTheme = useTheme();
  const [loading, setLoading] = useState(true);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
  const [patentes, setPatentes] = useState({});

  const obtenerPatentes = () => {
    setLoading(true); // Mostrar indicador de carga
    fetch(UrlApi + "/patentes_observaciones")
      .then((data) => data.json())
      .then((data) => {
        setPatentes(data);
        setLoading(false); // Ocultar indicador de carga
      });
  };

  useEffect(() => {
    obtenerPatentes();
  }, []);

  //*************************************************************************************************************************************/

  // DIALOG PARA DIALOG DE LAS OBSERVACIONES
  const [dialogObservaciones, setDialogObservaciones] = useState(false);

  // Abrir dialog
  const handleOpenDialogObservaciones = (vehiculo) => {
    setVehiculoSeleccionado(vehiculo);
    setDialogObservaciones(true);
  };

  // Cerrrar dialog
  const handleCloseDialogObservaciones = (vehiculo) => {
    setVehiculoSeleccionado(null);
    setDialogObservaciones(false);
  };

  //*************************************************************************************************************************************/

  // ----------- TABLA ---------------
  const columns = [
    { accessorKey: "ultima_fecha", header: "Fecha", size: 100 },
    { accessorKey: "patente", header: "Patente", size: 100 },
  ];

  const customLocalization = {
    ...MRT_Localization_ES,
    body: {
      ...MRT_Localization_ES.body,
      noData: "No hay registros para mostrar",
    },
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true, // Mostrar la barra de búsqueda
    sorting: [
      {
        id: "ultima_fecha",
        desc: false,
      },
    ],
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { backgroundColor: "#ffffff" },
    variant: "outlined",
    size: "small",
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  useEffect(() => {
  logEventos(
  "acceso al menú Observaciones de Vehículos",
   "Utilización de Vehículos",
    "Observaciones de Vehículos",
     localStorage.getItem("user"));
}, []);
  return (
    <>
      <Navbar />
      <Box
        sx={{
          marginTop: "20px",
          marginBottom: "25px",
          marginLeft: "40px",
          marginRight: "40px",
        }}
      >
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={columns}
            data={patentes}
            enableGlobalFilter={true}
            enableToolbarInternalActions={true}
            initialState={initialState}
            positionActionsColumn="last"
            autoResetPageIndex={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={true}
            enableColumnFilters={false}
            enableHiding={false}
            enableGrouping={true}
            localization={customLocalization}
            enableRowSelection={false}
            enableRowActions={true}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableBodyRowProps={({ table, row }) => {
              const rowIndex = table
                .getRowModel() //  Obtengo todas las filas visibles en el orden actual
                .rows.findIndex((r) => r.id === row.id); // Busca el índice real de la fila en la tabla
              return {
                sx: {
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f0f0f0", // Alternar los colores correctamente, incluso si hay agrupaciones o filtrados
                },
              };
            }}
            state={{ isLoading: loading }}
            muiCircularProgressProps={{
              color: "secondary",
              thickness: 5,
              size: 55,
            }}
            muiSkeletonProps={{
              animation: "pulse",
              height: 28,
            }}
            renderRowActions={({ row }) => (
              <>
                <Badge
                  badgeContent={row.original.conteo_observaciones}
                  onClick={() => handleOpenDialogObservaciones(row.original)}
                  // Abro el dialog (Mismo evento que el Button)
                  sx={{
                    cursor: "pointer", // Cambia el cursor a mano
                    "& .MuiBadge-badge": {
                      right: 3,
                      top: 8,
                      backgroundColor: "#D32F2F",
                      border: `2px solid`,
                      color: "white",
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => handleOpenDialogObservaciones(row.original)}
                    sx={{ mr: 1 }}
                  >
                    Ver Observaciones
                  </Button>
                </Badge>
              </>
            )}
            renderTopToolbarCustomActions={() => (
              <Typography variant="poster" fontSize={26}>
                Observaciones de Vehículos
              </Typography>
            )}
          />
        </ThemeProvider>
      </Box>

      {dialogObservaciones && (
        <ObservacionesRevision
          vehiculo={vehiculoSeleccionado}
          // ABRIR Y CERRAR DIALOG
          open={dialogObservaciones}
          onClose={handleCloseDialogObservaciones}
        />
      )}
    </>
  );
}

export default PatentesObservaciones;
